import { useIntl } from 'react-intl';

import { BUDGET_THRESHOLD_PERCENTAGE } from './constants';

import type { SourcingPurchaseDeactivationReason } from 'api-clients/monolith';

export const useAutomationMessages = () => {
  const intl = useIntl();

  const getAutomationMessages = (
    reason:
      | SourcingPurchaseDeactivationReason
      | null
      | 'target_budget_breached', // This is a custom reason for the banner to show when the budget is breached. Other reasons are from the backend.
    date?: Date | null,
    spendPercentage?: number,
  ) => {
    const formattedDate = date
      ? intl.formatDate(date, {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        })
      : null;

    switch (reason) {
      case 'automated_as_goal_met': {
        return {
          activeTitle: formattedDate
            ? intl.formatMessage(
                {
                  defaultMessage: 'Sourcing Resumed – {date}',
                  description:
                    'Automation banner title when sourcing resumed on a specific date',
                },
                { date: formattedDate },
              )
            : intl.formatMessage({
                defaultMessage: 'Sourcing Resumed',
                description:
                  'Short banner title when auto-sourcing turned back on (no specific date shown)',
              }),
          pausedTitle: formattedDate
            ? intl.formatMessage(
                {
                  defaultMessage: 'Sourcing Paused – {date}',
                  description:
                    'Automation banner title when sourcing paused on a specific date',
                },
                { date: formattedDate },
              )
            : intl.formatMessage({
                defaultMessage: 'Sourcing Paused',
                description:
                  'Short banner title when auto-sourcing is paused (no specific date shown)',
              }),
          activeDescription: intl.formatMessage({
            defaultMessage:
              'Sourcing has been resumed as we have insufficient applicants in the funnel.',
            description:
              'Explains auto-resume: funnel needed more applicants so sourcing turned back on',
          }),
          pausedDescription: intl.formatMessage({
            defaultMessage:
              'Sourcing has been paused automatically as we have sufficient applicants in the funnel.',
            description:
              'Explains auto-pause: funnel had enough applicants so spend stopped',
          }),
        };
      }

      case 'hiring_goal_ended': {
        return {
          activeTitle: intl.formatMessage({
            defaultMessage: 'Sourcing Active – New Hiring Goal Added',
            description:
              'Title when a new hiring period started and campaigns may have been toggled',
          }),
          pausedTitle: intl.formatMessage({
            defaultMessage: 'Sourcing Paused – Hiring Goal Ended',
            description:
              'Title when the hiring period ended and campaigns were paused for this opening',
          }),
          activeDescription: intl.formatMessage({
            defaultMessage:
              'A new hiring goal has started, and some campaigns were affected.',
            description:
              'Body copy: goal change impacted which campaigns are running',
          }),
          pausedDescription: intl.formatMessage({
            defaultMessage:
              'The hiring goal for this opening has ended. All campaigns have been paused. Set a new hiring goal to continue sourcing.',
            description:
              'Body copy: no active goal, user must set a new goal to resume',
          }),
        };
      }

      case 'target_budget_met': {
        return {
          activeTitle: intl.formatMessage({
            defaultMessage: 'Sourcing Active – Budget Updated',
            description:
              'Title after the account raised budget and automation turned sourcing back on',
          }),
          pausedTitle: intl.formatMessage({
            defaultMessage: 'Sourcing Paused – Target budget reached',
            description:
              'Title when spend hit the cap and automation paused campaigns',
          }),
          activeDescription: intl.formatMessage({
            defaultMessage:
              "Your hiring budget has increased. We've automatically resumed campaigns paused by budget automation.",
            description:
              'Explains that more budget was added so previously paused runs resumed',
          }),
          pausedDescription: intl.formatMessage({
            defaultMessage:
              "You've used 100% of your target budget. Campaigns are paused automatically to prevent overspending. Update the budget to resume sourcing.",
            description:
              'Explains 100% spend: campaigns stopped until the user changes the cap',
          }),
        };
      }

      case 'target_budget_breached': {
        return {
          activeTitle: intl.formatMessage({
            defaultMessage: 'Approaching Budget Limit',
            description:
              'Title when user is near the configured budget auto-pause threshold',
          }),
          pausedTitle: '',
          activeDescription: intl.formatMessage(
            {
              defaultMessage:
                "You've used {spendPercentage}% of your target budget. Campaigns will auto-pause when the limit is reached. Update budget to continue hiring.",
              description:
                'Body text when the account is approaching the target budget cap before auto-pause',
            },
            {
              spendPercentage:
                spendPercentage ?? BUDGET_THRESHOLD_PERCENTAGE * 100,
            },
          ),
          pausedDescription: '',
        };
      }

      default: {
        return null;
      }
    }
  };

  return { getAutomationMessages };
};
